import { Injectable, Inject } from '@angular/core';
import { UserChannelTermsService } from '@app/services/user-channel-terms.service';
import { UserLanguageTermService } from '@app/services/user-language-term.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { CacheService } from '@app/services/cache.service';
import { FeatureSettingsStoreService } from '@app/services/feature-settings-store.service';
import { ChannelTermInfoBox, InfoB, InfoBoxArea, InfoBoxHeight, InfoBoxStyle, InfoBoxType, InfoBoxWidth } from '@app/models/infobox';
import { Constants } from '@app/common/constants';
import { Subject, BehaviorSubject, Observable, concat, of, forkJoin } from 'rxjs';
import { map, tap, catchError, takeUntil, shareReplay, mergeMap, filter, concatMap, first } from 'rxjs/operators';
import { HttpHelper } from '@app/common/http-helper';
import { Environment } from '@environments/interface.environment';
import { CUSTOMERENVIRONMENT } from '@app/common/app-tokens';
import { applyLanguageFilteringSingle } from '@app/common/filtering-behavior';
import { ComponentStatesOnRoutesService } from '@app/services/component-states-on-routes.service';
import { ListItem } from '@microsoft/microsoft-graph-types';
import { ImageService } from '@app/services/image.service';

@Injectable({
  providedIn: 'root'
})
export class InfoboxService {

  constructor(
    @Inject(CUSTOMERENVIRONMENT) private environment: Environment,
    private http: HttpClient,
    private userChannelTermsService: UserChannelTermsService,
    private userLanguageTermService: UserLanguageTermService,
    private featureSettingsStoreService: FeatureSettingsStoreService,
    private componentStatesOnRoutesService: ComponentStatesOnRoutesService,
    private httpHelper: HttpHelper,
    private cache: CacheService,
    private imageService: ImageService) {

    this.componentStatesOnRoutesService
      .state$
      .pipe(
        filter(componentsOnRouteState => {
          let shouldReload = false;
          if (componentsOnRouteState) {
            componentsOnRouteState.RouteMap.forEach(val => {
              if (val.some(s => s.ComponentId === 'app-infobox-zone' && s.Refresh)) {
                shouldReload = true;
              }
            });
          }
          return shouldReload;
        }))
      .subscribe(componentsOnRouteState => {
        if (componentsOnRouteState) {
          this.reload();
        }
      });
  }

  private readonly cacheKey = 'InfoboxService-getInfoBoxes';
  private readonly myActiveCacheKey = `${this.cacheKey}-active`;
  private readonly futureCacheKey = `${this.cacheKey}-future`;
  private readonly allCacheKey = `${this.cacheKey}-all`;
  public useCache = true;
  private reload$ = new Subject<void>();
  public refresh$ = new BehaviorSubject(null);
  currentEditMode$ = new BehaviorSubject<boolean>(false);
  editMode = this.currentEditMode$.value;

  getAllInfoBoxes$: Observable<Array<InfoB>>;
  private getMyActiveInfoBoxes$ = new Map<string, Observable<Array<InfoB>>>();
  private getActiveAndFutureInfoBoxes$ = new Map<string, Observable<Array<InfoB>>>();

  public reload() {
    this.getMyActiveInfoBoxes$.clear();
    this.getActiveAndFutureInfoBoxes$.clear();
    this.getAllInfoBoxes$ = null;
    if (this.useCache) {
      this.cache.removeAllItemsInPersistentCacheWithGeneratedKeyPrefix(this.cacheKey);
    }
    this.reload$.next();
    this.refresh$.next(null);
  }

  getMyActiveInfoBoxes(route: string, area: string): Observable<Array<InfoB>> {
    const cacheKey = `${this.myActiveCacheKey}-${route}-${area}`;
    if (this.getMyActiveInfoBoxes$.has(cacheKey)) {
      return this.getMyActiveInfoBoxes$.get(cacheKey);
    } else {
      const httpParams = new HttpParams().append('route', route).append('area', area);
      const infoBoxes$ = forkJoin([
        this.http.get<Array<InfoB>>(`${Constants.API_ENDPOINT}/infoboxes`, { params: httpParams }),
        this.userChannelTermsService.getUserChannelTerms()
      ])
        .pipe(
          map(([response, userChannelTerms]) => {
            console.log(`fetched result getMyActiveInfoBoxes: `, response);
            return response
              .map(p => new InfoB(p))
              .filter(infoBox => {
                const channelTerms = infoBox.ChannelTermInfoBoxes;
                if (channelTerms && channelTerms.length > 0) {
                  let found = true;
                  const matchingChannels = channelTerms.filter(channel => {
                    found = userChannelTerms.some(ct => ct.ChannelTermId === channel.ChannelTermId);
                    if (found) {
                      return channel;
                    }
                  });
                  if (matchingChannels && matchingChannels.length > 0) {
                    return infoBox;
                  }
                } else {
                  return infoBox;
                }
              });
          })
        );

      this.getMyActiveInfoBoxes$.set(cacheKey,
        infoBoxes$
          .pipe(
            mergeMap((infoBoxes) => {
              infoBoxes = infoBoxes.filter(ib => !ib.Hidden);
              // Language filter toggle
              if (this.featureSettingsStoreService.state.enableLanguageFilteringForWorkplaceInfoboxes) {
                return this.userLanguageTermService
                  .getUserLanguageTerms()
                  .pipe(
                    map((userLanguageTerms) => {
                      return applyLanguageFilteringSingle(infoBoxes, userLanguageTerms, this.featureSettingsStoreService.state.enableShowNonLanguageContentForAllUsers, this.featureSettingsStoreService.state.defaultLanguageTermId);
                    }));
              } else {
                return of(infoBoxes);
              }
            }),
            tap(data => {
              this.useCache && this.cache.set(cacheKey, data);
            }),
            catchError(this.httpHelper.handleError({ operation: 'getMyActiveInfoBoxes', result: new Array<InfoB>(), cacheKey: cacheKey })),
            takeUntil(this.reload$),
            shareReplay({ bufferSize: 1, refCount: true })));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<InfoB>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getMyActiveInfoBoxes$.get(cacheKey));
        }
      }
      return this.getMyActiveInfoBoxes$.get(cacheKey);
    }
  }

  getActiveAndFutureInfoBoxes(route: string, area: string): Observable<Array<InfoB>> {
    const cacheKey = `${this.futureCacheKey}-${route}-${area}`;
    if (this.getActiveAndFutureInfoBoxes$.has(cacheKey)) {
      return this.getActiveAndFutureInfoBoxes$.get(cacheKey);
    } else {
      const httpParams = new HttpParams()
        .append('route', route)
        .append('area', area)
        .append('includeFuture', 'true');

      const infoBoxes$ = forkJoin([
        this.http.get<Array<InfoB>>(`${Constants.API_ENDPOINT}/infoboxes`, { params: httpParams })
      ])
        .pipe(
          map(([response]) => {
            console.log(`fetched result getActiveAndFutureInfoBoxes: `, response);
            return response
              .map(p => new InfoB(p));
          })
        );

      this.getActiveAndFutureInfoBoxes$.set(cacheKey,
        infoBoxes$
          .pipe(
            mergeMap((infoBoxes) => {
              infoBoxes = infoBoxes.filter(ib => !ib.Hidden);
              // Language filter toggle
              if (this.featureSettingsStoreService.state.enableLanguageFilteringForWorkplaceInfoboxes) {
                return this.userLanguageTermService
                  .getUserLanguageTerms()
                  .pipe(
                    map((userLanguageTerms) => {
                      return applyLanguageFilteringSingle(infoBoxes, userLanguageTerms, this.featureSettingsStoreService.state.enableShowNonLanguageContentForAllUsers, this.featureSettingsStoreService.state.defaultLanguageTermId);
                    }));
              } else {
                return of(infoBoxes);
              }
            }),
            tap(data => {
              this.useCache && this.cache.set(cacheKey, data);
            }),
            catchError(this.httpHelper.handleError({ operation: 'getFutureInfoBoxes', result: new Array<InfoB>(), cacheKey: cacheKey })),
            takeUntil(this.reload$),
            shareReplay({ bufferSize: 1, refCount: true })));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<InfoB>>(cacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getActiveAndFutureInfoBoxes$.get(cacheKey));
        }
      }
      return this.getActiveAndFutureInfoBoxes$.get(cacheKey);
    }
  }

  getInfoBox(id: number): Observable<InfoB> {
    return this.http.get<InfoB>(`${Constants.API_ENDPOINT}/infoboxes/${id}`)
      .pipe(
        map(response => {
          console.log(`fetched result getInfoBox: `, response);
          return new InfoB(response);
        }),
        catchError(this.httpHelper.handleError({ operation: 'getInfoBox', result: null }))
      );
  }

  getInfoBoxAreas(): Observable<Array<InfoBoxArea>> {
    return this.http.get<InfoBoxArea>(`${Constants.API_ENDPOINT}/infoboxes/areas`)
      .pipe(
        catchError(this.httpHelper.handleError({ operation: 'getInfoBoxAreas', result: null }))
      );
  }

  getAllInfoBoxes(filterOnLanguageIfEnabled = false): Observable<Array<InfoB>> {
    if (this.getAllInfoBoxes$) {
      return this.getAllInfoBoxes$;
    } else {
      this.getAllInfoBoxes$ = this.http.get<Array<InfoB>>(`${Constants.API_ENDPOINT}/infoboxes/all`)
        .pipe(
          map(response => {
            console.log(`fetched result getAllInfoBoxes: `, response);
            return response.map(p => new InfoB(p));
          }),
          mergeMap(boxes => {
            // Language filter toggle
            if (filterOnLanguageIfEnabled && this.featureSettingsStoreService.state.enableLanguageFilteringForWorkplaceInfoboxes) {
              return this.userLanguageTermService
                .getUserLanguageTerms()
                .pipe(
                  map((userLanguageTerms) => {
                    return applyLanguageFilteringSingle(boxes, userLanguageTerms, this.featureSettingsStoreService.state.enableShowNonLanguageContentForAllUsers, this.featureSettingsStoreService.state.defaultLanguageTermId);
                  }));
            } else {
              return of(boxes);
            }
          }),
          tap(data => {
             this.useCache && this.cache.set(this.allCacheKey, data);
          }),
          catchError(this.httpHelper.handleError({ operation: 'getAllInfoBoxes', result: new Array<InfoB>(), cacheKey: this.allCacheKey })),
          takeUntil(this.reload$),
          shareReplay({ bufferSize: 1, refCount: true }));

      if (this.useCache) {
        const cachedData = this.cache.get<Array<InfoB>>(this.allCacheKey);
        if (cachedData) {
          return concat(of(cachedData), this.getAllInfoBoxes$);
        }
      }
      return this.getAllInfoBoxes$;
    }
  }

  updateInfoboxes(infoBoxes: Array<InfoB>): Observable<Array<InfoB>> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.put<Array<InfoB>>(`${Constants.API_ENDPOINT}/infoboxes`, infoBoxes, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`updated result: `);
          console.log(response);
          this.reload();
          return infoBoxes;
        }),
        catchError(this.httpHelper.handleError({ operation: 'updateInfoboxes', result: ({} as InfoB[]), rethrow: true })));

    return fetchOperation;
  }

  updateInfobox(id: number, infobox: InfoB): Observable<InfoB> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.put<InfoB>(`${Constants.API_ENDPOINT}/infoboxes/${id}`, infobox, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`updated result: `);
          console.log(response);
          this.reload();
          return infobox;
        }));

    return fetchOperation;
  }

  deleteInfobox(id: number): Observable<InfoB> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.delete<InfoB>(`${Constants.API_ENDPOINT}/infoboxes/${id}`, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`deleted result: `);
          console.log(response);
          this.reload();
          return response;
        }),
        catchError(this.httpHelper.handleError({ operation: 'deleteInfobox', result: new InfoB() })));

    return fetchOperation;
  }

  hideInfobox(id: number): Observable<any> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const updateOperation = this.http.put<InfoB>(`${Constants.API_ENDPOINT}/infoboxes/${id}/hide`, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`updated result: `);
          console.log(response);
          this.reload();
          return response;
        }));

    return updateOperation;
  }

  addInfobox(infoBox: InfoB): Observable<InfoB> {
    // We need to append the siteUrl because there will be an authorization check in the backend against the workplace admin group on this site
    const httpParams = new HttpParams().append('siteUrl', encodeURI(this.environment.coreWorkplaceSiteUrl));
    const fetchOperation = this.http.post<InfoB>(`${Constants.API_ENDPOINT}/infoboxes`, infoBox, {
      params: httpParams
    })
      .pipe(
        map(response => {
          console.log(`added result: `);
          console.log(response);
          this.reload();
          return new InfoB(response);
        }));

    return fetchOperation;
  }

  getInfoboxesFromGraphAndCopyToWorkplace() {
    const listsRequest = `${this.environment.coreWorkplaceSiteUrl}/_api/v2.0/sharePoint/getBaseItemByUrl(url='/lists/InfoBoxes')/items`;
    const now = new Date().toISOString();
    const httpParams = new HttpParams()
      .append('expand', 'fields')
      .append('filter', '(fields/BoolField_ContentStartDate le \'' + now + '\' or fields/BoolField_ContentStartDate eq null) and (fields/BoolField_ContentEndDate ge \'' + now + '\' or fields/BoolField_ContentEndDate eq null)')
      .append('orderby', 'fields/Modified desc')
      .append('select', 'fields,id,parentReference,contentType')
      .append('top', '500');

    return this.http
      .get<any>(listsRequest, {
        params: httpParams,
        headers: {
          'Prefer': 'HonorNonIndexedQueriesWarningMayFailRandomly'
        }
      })
      .pipe(
        concatMap((listItemResult) => {
          const mappedItems = (listItemResult.value as ListItem[])
            .map(item => {
              const contentType = item.contentType.id;
              let typeOfInfoBox: InfoBoxType;
              let imageUrlToFetch: string;
              if (contentType.indexOf(Constants.CT_WORKPLACE_INFOBOX_TEXT_GUID) > -1) {
                typeOfInfoBox = InfoBoxType.Text;
              }
              if (contentType.indexOf(Constants.CT_WORKPLACE_INFOBOX_PICTURE_GUID) > -1) {
                typeOfInfoBox = InfoBoxType.Picture;
                imageUrlToFetch = `${this.environment.coreWorkplaceSiteUrl}/_api/web/lists(guid'${item.parentReference['listId']}')/items(${item.id})/FieldValuesAsHtml?$select=BoolField_InfoboxPicture`;
              }
              if (contentType.indexOf(Constants.CT_WORKPLACE_INFOBOX_VIDEO_GUID) > -1) {
                typeOfInfoBox = InfoBoxType.Video;
                imageUrlToFetch = `${this.environment.coreWorkplaceSiteUrl}/_api/web/lists(guid'${item.parentReference['listId']}')/items(${item.id})/FieldValuesAsHtml?$select=BoolField_InfoboxPicture`;
              }

              if (imageUrlToFetch) {
                return this.imageService.getPublishingImageUrl(imageUrlToFetch, undefined)
                  .pipe(
                    first(),
                    map(url => {
                      return this.buildOldInfoBox(item, typeOfInfoBox, url);
                    })
                  );
              } else {
                return of(this.buildOldInfoBox(item, typeOfInfoBox));
              }
            });
          return concat(...mappedItems);
        }),
        concatMap((item) => {
          const newItem = new InfoB({
            Type: item.Type,
            Title: item.Title,
            ShowTitle: item.ApplyTitle,
            Content: item.Text,
            LinkUrl: item.LinkUrl,
            LinkDescription: item.LinkDescription,
            PictureUrl: item.ImageUrl,
            VideoEmbedCode: item.VideoEmbed,
            Style: this.getStyle(item.WorkplaceStyle),
            Width: this.getWidth(item.WorkplaceSize),
            Height: (item.WorkplaceArea as string).toLowerCase().indexOf('compact') > -1 ? InfoBoxHeight.Compact : InfoBoxHeight.Normal,
            Order: this.getOrder(item.WorkplaceOrder),
            StartDate: item.StartDate,
            EndDate: item.EndDate,
            ChannelTermInfoBoxes: this.getChannels(item.Channels),
            LanguageTermId: item.LanguageTermId,
            AreaId: this.getArea(item.WorkplaceArea),
            RouteId: this.getRouteId(item.WorkplaceArea)
          });
          return this.addInfobox(newItem);
        })
      );
  }

  getChannels(channels: Array<any>) {
    return channels?.map(c => {
      return {
        ChannelTermId: c.TermGuid
      } as ChannelTermInfoBox;
    });
  }

  getRouteId(area: string) {
    const areaInLowerCase = area.toLowerCase();
    if (areaInLowerCase.indexOf('hero') > -1) {
      return '/';
    }

    if (areaInLowerCase === 'workplace' || areaInLowerCase === 'workplace start') {
      return '/';
    }

    if (areaInLowerCase.indexOf('collab') > -1) {
      return '/collaboration';
    }

    if (areaInLowerCase.indexOf('board') > -1) {
      return '/your-board';
    }

    if (areaInLowerCase === 'find information') {
      return '/find-information';
    }

    if (areaInLowerCase.indexOf('welcome') > -1) {
      return '/first-day';
    }

    return area;
  }

  getArea(area: string) {
    const areaInLowerCase = area.toLowerCase();

    if (areaInLowerCase.indexOf('hero') > -1) {
      return 'Hero';
    }

    if (areaInLowerCase === 'workplace' || areaInLowerCase === 'workplace start') {
      return 'Default';
    }

    if (areaInLowerCase.indexOf('collab') > -1) {
      return 'Collaboration';
    }

    if (areaInLowerCase.indexOf('board') > -1) {
      return 'YourBoard';
    }

    if (areaInLowerCase === 'find information') {
      return 'FindInformation';
    }

    if (areaInLowerCase.indexOf('welcome') > -1) {
      return 'Welcome';
    }

    return area;
  }

  getStyle(style: string) {
    const styleInLower = style.toLowerCase();
    switch (styleInLower) {
      case '1. none':
        return InfoBoxStyle.None;
      case '2. accent 1':
        return InfoBoxStyle.Accent1;
      case '3. accent 2':
        return InfoBoxStyle.Accent2;
      case '4. accent 3':
        return InfoBoxStyle.Accent3;
      default:
        return InfoBoxStyle.None;
    }
  }

  getWidth(size: string) {
    const sizeInLower = size.toLowerCase();
    switch (sizeInLower) {
      case 'auto':
        return InfoBoxWidth.Auto;
      case '25%':
        return InfoBoxWidth.Width25Percent;
      case '33%':
        return InfoBoxWidth.Width33Percent;
      case '50%':
        return InfoBoxWidth.Width50Percent;
      case '66%':
        return InfoBoxWidth.Width66Percent;
      case '75%':
        return InfoBoxWidth.Width75Percent;
      case '100%':
        return InfoBoxWidth.Width100Percent;
      default:
        return InfoBoxWidth.Auto;
    }
  }

  getOrder(order: string): number {
    const firstTwoNumbers = Number.parseInt(order.substr(0, 2), 10);
    if (!isNaN(firstTwoNumbers)) {
      return firstTwoNumbers;
    }

    const firstNumber = Number.parseInt(order.substr(0, 1), 10);
    if (!isNaN(firstNumber)) {
      return firstNumber;
    }

    return 9999;
  }

  private buildOldInfoBox(item: ListItem, typeOfInfoBox: InfoBoxType, url?: string) {
    return {
      Title: item.fields['BoolField_InfoBoxTitle'],
      ApplyTitle: item.fields['BoolField_InfoBoxApplyHeading'] !== undefined ? item.fields['BoolField_InfoBoxApplyHeading'] : true, // Setting true when undefined to handle existing items
      StartDate: item.fields['BoolField_ContentStartDate'],
      EndDate: item.fields['BoolField_ContentEndDate'],
      WorkplaceArea: item.fields['BoolField_InfoboxArea'],
      WorkplaceOrder: item.fields['BoolField_InfoboxOrder'],
      WorkplaceSize: item.fields['BoolField_InfoboxSize'],
      WorkplaceStyle: item.fields['BoolField_InfoboxStyle'],
      LinkUrl: item.fields['BoolField_InfoBoxLinkUrl'] ? item.fields['BoolField_InfoBoxLinkUrl'].Url : null,
      LinkDescription: item.fields['BoolField_InfoBoxLinkUrl'] ? item.fields['BoolField_InfoBoxLinkUrl'].Description : null,
      Text: item.fields['BoolField_InfoBoxTextContent'],
      VideoEmbed: item.fields['BoolField_InfoBoxVideoEmbed'],
      Channels: item.fields['BoolField_Channel'],
      LanguageTermId: (item.fields['BoolField_Language'] && item.fields['BoolField_Language'].TermGuid) ? item.fields['BoolField_Language'].TermGuid : undefined,
      Type: typeOfInfoBox,
      ImageUrl: url
    };
  }

}
